import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  DISCARD_REASONS,
  productionDiscardApi,
} from "@/features/production/services/productionDiscardApi";
import { queryClient } from "@/shared/services/queryClient";
import { useAgroforestryStockPaged, useMarkReadyForSale } from "../hooks/useAgroforestry";
import type { AgroforestryStockEntryDto } from "../services/agroforestryApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatNumber = (value: number) =>
  value.toLocaleString("en-KE", { maximumFractionDigits: 2 });

export const AgroforestryStockPage = () => {
  const notify = useNotification();
  const markReadyMutation = useMarkReadyForSale();
  const { hasPermission } = useAuthStore();

  const canMarkReady = hasPermission("agroforestry.mark_ready_for_sale");

  const [discardOpen, setDiscardOpen] = useState(false);
  const [discardEntry, setDiscardEntry] = useState<AgroforestryStockEntryDto | null>(null);
  const [discardQty, setDiscardQty] = useState("");
  const [discardReason, setDiscardReason] = useState<string>(String(DISCARD_REASONS[6].value)); // Disease
  const [discardDetails, setDiscardDetails] = useState("");
  const [submittingDiscard, setSubmittingDiscard] = useState(false);

  // Mark ready dialog state
  const [readyOpen, setReadyOpen] = useState(false);
  const [readyEntry, setReadyEntry] = useState<AgroforestryStockEntryDto | null>(null);
  const [readyQty, setReadyQty] = useState("");

  // Pagination & filtering
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "healing" | "ready">("all");

  const stockQuery = useAgroforestryStockPaged({
    pageNumber: page,
    pageSize,
    search: search || undefined,
    status: tab === "all" ? undefined : tab,
  });

  const rows = stockQuery.data?.items ?? [];
  const totalCount = stockQuery.data?.totalCount ?? 0;
  const totalPages = stockQuery.data?.totalPages ?? 0;

  const openMarkReady = (entry: AgroforestryStockEntryDto) => {
    setReadyEntry(entry);
    setReadyQty(String(entry.availableQuantity));
    setReadyOpen(true);
  };

  const submitMarkReady = () => {
    if (!readyEntry) return;
    const qty = Number(readyQty);
    if (qty <= 0 || qty > readyEntry.availableQuantity) return;
    markReadyMutation.mutate(
      { entryId: readyEntry.id, quantity: qty },
      {
        onSuccess: () => {
          notify.success(`${qty} marked ready for sale.`);
          setReadyOpen(false);
          setReadyEntry(null);
        },
        onError: () => notify.error("Failed to mark ready for sale."),
      },
    );
  };

  const openDiscard = (entry: AgroforestryStockEntryDto) => {
    setDiscardEntry(entry);
    setDiscardQty("");
    setDiscardReason(String(DISCARD_REASONS[6].value));
    setDiscardDetails("");
    setDiscardOpen(true);
  };

  const submitDiscard = async () => {
    if (!discardEntry) return;
    const qty = Number(discardQty);
    if (!(qty > 0)) {
      notify.error("Quantity must be greater than zero.");
      return;
    }
    const available = discardEntry.totalQuantity - discardEntry.dispatchedQuantity;
    if (qty > available) {
      notify.error(`Cannot discard more than the ${formatNumber(available)} remaining on this batch.`);
      return;
    }
    setSubmittingDiscard(true);
    try {
      await productionDiscardApi.create({
        greenhouseStockEntryId: discardEntry.id,
        quantityToDiscard: qty,
        reason: Number(discardReason),
        reasonDetails: discardDetails.trim() || undefined,
        requiresApproval: true,
      });
      notify.success("Write-off submitted — awaiting approval.");
      // Refresh agroforestry stock + the GM's pending discard queue.
      queryClient.invalidateQueries({ queryKey: ["agroforestry-stock"] });
      queryClient.invalidateQueries({ queryKey: ["production-discards", "pending"] });
      setDiscardOpen(false);
      setDiscardEntry(null);
    } catch {
      notify.error("Failed to submit write-off.");
    } finally {
      setSubmittingDiscard(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Agroforestry Stock</h1>
        <p className="mt-1 text-muted-foreground">
          Tree-seedling batches received from external suppliers and currently held on the farm.
          Mark each batch ready for sale once it has healed; the walk-in POS picks it up
          automatically when variety pricing is set.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active batches</CardTitle>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Tabs value={tab} onValueChange={(v) => { setTab(v as typeof tab); setPage(1); }}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="healing">Healing</TabsTrigger>
                <TabsTrigger value="ready">Ready</TabsTrigger>
              </TabsList>
            </Tabs>
            <Input
              placeholder="Search crop or variety..."
              className="max-w-xs"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </CardHeader>
        <CardContent>
          {stockQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!stockQuery.isLoading && totalCount === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              {search ? "No matching batches found." : "No agroforestry batches yet. Submit a receipt to get started."}
            </p>
          )}
          {rows.length > 0 && (
            <>
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Receipt #</TableHead>
                    <TableHead>Crop / Variety</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Discarded</TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead className="text-right">Reserved</TableHead>
                    <TableHead className="text-right">Dispatched</TableHead>
                    <TableHead>Received date</TableHead>
                    <TableHead>Unit cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium">{entry.receiptNumber ?? "—"}</TableCell>
                      <TableCell>
                        {entry.cropTypeName}
                        {entry.variety ? ` — ${entry.variety}` : ""}
                      </TableCell>
                      <TableCell className="text-right">{formatNumber(entry.originalQuantity)}</TableCell>
                      <TableCell className="text-right text-red-600">
                        {entry.totalDiscarded > 0 ? formatNumber(entry.totalDiscarded) : "—"}
                      </TableCell>
                      <TableCell className="text-right font-semibold">{formatNumber(entry.availableQuantity)}</TableCell>
                      <TableCell className="text-right">{formatNumber(entry.reservedQuantity)}</TableCell>
                      <TableCell className="text-right">{formatNumber(entry.dispatchedQuantity)}</TableCell>
                      <TableCell>{formatDate(entry.entryDate)}</TableCell>
                      <TableCell>{entry.unitCost != null ? `KES ${formatNumber(entry.unitCost)}` : "—"}</TableCell>
                      <TableCell>
                        {entry.isReadyForDispatch ? (
                          <Badge className="border-green-400 bg-green-100 text-green-700">Ready</Badge>
                        ) : (
                          <Badge variant="outline">Healing</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!entry.isReadyForDispatch && canMarkReady && (
                            <Button
                              size="sm"
                              onClick={() => openMarkReady(entry)}
                              disabled={markReadyMutation.isPending}
                            >
                              Mark ready for sale
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openDiscard(entry)}
                          >
                            Request write-off
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount} results
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50].map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>{"<"}</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{">"}</Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={discardOpen}
        onOpenChange={(o) => {
          setDiscardOpen(o);
          if (!o) setDiscardEntry(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request write-off</DialogTitle>
            <DialogDescription>
              {discardEntry
                ? `${discardEntry.cropTypeName}${discardEntry.variety ? ` — ${discardEntry.variety}` : ""} (receipt ${discardEntry.receiptNumber ?? "—"})`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="discardQty">Quantity (pieces)</FieldLabel>
              <Input
                id="discardQty"
                type="number"
                inputMode="decimal"
                min="1"
                max={discardEntry?.availableQuantity ?? undefined}
                step="0.01"
                value={discardQty}
                onChange={(e) => setDiscardQty(e.target.value)}
              />
              {discardEntry && (
                <p className="text-xs text-muted-foreground">
                  Max: {discardEntry.availableQuantity} available
                </p>
              )}
              {Number(discardQty) > (discardEntry?.availableQuantity ?? 0) && (
                <p className="text-xs text-destructive">Cannot exceed available quantity</p>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="discardReason">Reason</FieldLabel>
              <Select value={discardReason} onValueChange={setDiscardReason}>
                <SelectTrigger id="discardReason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISCARD_REASONS.map((r) => (
                    <SelectItem key={r.value} value={String(r.value)}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="discardDetails">Details</FieldLabel>
              <Textarea
                id="discardDetails"
                rows={3}
                placeholder="e.g. Wilt outbreak in north section"
                value={discardDetails}
                onChange={(e) => setDiscardDetails(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardOpen(false)} disabled={submittingDiscard}>
              Cancel
            </Button>
            <Button
              onClick={submitDiscard}
              disabled={
                submittingDiscard ||
                Number(discardQty) <= 0 ||
                Number(discardQty) > (discardEntry?.availableQuantity ?? 0)
              }
            >
              {submittingDiscard ? "Submitting..." : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark ready for sale dialog */}
      <Dialog
        open={readyOpen}
        onOpenChange={(o) => {
          setReadyOpen(o);
          if (!o) setReadyEntry(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark ready for sale</DialogTitle>
            <DialogDescription>
              {readyEntry
                ? `${readyEntry.cropTypeName}${readyEntry.variety ? ` — ${readyEntry.variety}` : ""} (receipt ${readyEntry.receiptNumber ?? "—"})`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="readyQty">Quantity to mark ready</FieldLabel>
              <Input
                id="readyQty"
                type="number"
                inputMode="decimal"
                min="1"
                max={readyEntry?.availableQuantity ?? undefined}
                step="1"
                value={readyQty}
                onChange={(e) => setReadyQty(e.target.value)}
              />
              {readyEntry && (
                <p className="text-xs text-muted-foreground">
                  Available: {formatNumber(readyEntry.availableQuantity)} — mark all or a portion.
                </p>
              )}
              {Number(readyQty) > (readyEntry?.availableQuantity ?? 0) && (
                <p className="text-xs text-destructive">Cannot exceed available quantity</p>
              )}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReadyOpen(false)} disabled={markReadyMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={submitMarkReady}
              disabled={
                markReadyMutation.isPending ||
                Number(readyQty) <= 0 ||
                Number(readyQty) > (readyEntry?.availableQuantity ?? 0)
              }
            >
              {markReadyMutation.isPending ? "Marking..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgroforestryStockPage;
